import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase/client'
import { syncUserLanguage } from '../../lib/supabase/auth'
import { useLanguage } from '../../contexts/LanguageProvider'

/**
 * OAuth Callback Page
 *
 * Handles OAuth callbacks from the auth-landing server for Google/Apple login.
 *
 * Flow:
 * 1. User clicks "Sign in with Google" on SetupPage
 * 2. Opens oauth.happytask.app/google?platform=web
 * 3. User completes Google authentication
 * 4. OAuth server redirects to: https://app.happytask.app/auth/callback?access_token=...&refresh_token=...
 * 5. This page extracts tokens from query params and calls supabase.auth.setSession()
 * 6. Redirects to /home after successful session establishment
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { language } = useLanguage()

  useEffect(() => {
    const processOAuthCallback = async () => {

      // Extract tokens from query parameters
      const urlParams = new URLSearchParams(search)
      const accessToken = urlParams.get('access_token')
      const refreshToken = urlParams.get('refresh_token')
      const error = urlParams.get('error')
      const errorDescription = urlParams.get('error_description')

      // Handle OAuth errors
      if (error) {
        console.error('[OAuthCallback] OAuth error:', error, errorDescription)
        // Redirect back to setup with error message
        navigate('/?error=' + encodeURIComponent(errorDescription || error), { replace: true })
        return
      }

      // Validate tokens
      if (!accessToken || !refreshToken) {
        console.error('[OAuthCallback] Missing tokens in callback URL')
        navigate('/?error=missing_tokens', { replace: true })
        return
      }


      try {
        // Set Supabase session with OAuth tokens
        const { data, error: sessionError } = await supabase!.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })

        if (sessionError) {
          console.error('[OAuthCallback] Failed to set session:', sessionError)
          navigate('/?error=' + encodeURIComponent(sessionError.message), { replace: true })
          return
        }

        // is_active gate — OAuth bypasses the Password-Verification Hook,
        // so we have to check the flag client-side after the session is
        // set. AuthProvider's onAuthStateChange will ALSO catch this
        // (defense in depth), but checking here first avoids navigating
        // to /home only to be kicked back, and skips the user_metadata
        // sync for an account that's not allowed in.
        if (data.user?.id) {
          const { data: profile } = await supabase!
            .from('profiles')
            .select('is_active')
            .eq('id', data.user.id)
            .maybeSingle()
          if (profile?.is_active === false) {
            await supabase!.auth.signOut()
            navigate('/', { replace: true })
            return
          }
        }

        // Sprache in user_metadata setzen (Default: aktuelle App-Sprache)
        await syncUserLanguage(language)

        // Set loginMethod flag for OAuth flow
        localStorage.setItem('loginMethod', 'oauth')

        // Check if user has full_name from OAuth provider (Google, Apple)
        // If yes, auto-set profileSetupCompleted flag
        if (data.user?.user_metadata?.full_name) {
          localStorage.setItem('profileSetupCompleted', 'true')
        }

        // Clean URL (remove tokens from query params)
        window.history.replaceState(null, '', '/auth/callback')

        // Redirect to home (ProtectedRoute will handle profile setup flow if needed)
        navigate('/home', { replace: true })
      } catch (err) {
        console.error('[OAuthCallback] Unexpected error:', err)
        navigate('/?error=unexpected_error', { replace: true })
      }
    }

    processOAuthCallback()
  }, [search, navigate, language])

  // Show loading spinner while processing
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      gap: '16px',
      fontSize: '16px',
      color: 'var(--muted-foreground)'
    }}>
      <div className="spinner" style={{
        width: '40px',
        height: '40px',
        border: '4px solid rgba(0, 0, 0, 0.1)',
        borderTopColor: '#000',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <p>Completing sign-in…</p>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
